import { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { IconProps } from '@ui-kitten/components';

import CalendarIcon from '../../../Assets/calendar.svg';
import MoneyIcon from '../../../Assets/money.svg';
import Button from '../../../Components/Button';
import Image from '../../../Components/Image';
import ListItem from '../../../Components/ListItem';
import ReadMore from '../../../Components/ReadMore';
import { textCategory } from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import Tickets from '../../../Components/SVG/Tickets';
import constants from '../../../Config/constants';
import { getEventPriceRange } from '../../../Helper';
import DateHelper from '../../../Helper/Date';
import { EventDetails } from '../../../RTK/api/events/types';
import themeColor from '../../../Theme/colors';
import themeStyle from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../../Theme/styles/platform';

type Props = EventDetails & {
  onBookNow: (data: any) => void;
};

const EventItem = (props: Props) => {
  const { onBookNow, ...data } = props;
  const {
    start_date,
    end_date,
    description,
    image,
    name,
    tickets,
    available_days,
    time_slots,
  } = data;
  const { isLastStart, start, startFormatted } = useMemo(
    () =>
      DateHelper.getEventScheduleInfo(
        start_date,
        end_date,
        time_slots,
        available_days,
      ),
    [start_date, end_date, time_slots, available_days],
  );
  const prices = useMemo(() => getEventPriceRange(tickets), [tickets]);
  const Accessoryleft = useCallback((leftIconProps: IconProps) => {
    return (
      <Tickets
        {...leftIconProps}
        fill={themeColor.white}
        style={[
          themeStyle.spacingLeftSmall,
          themeStyle.spacingRightSmall,
          styles.ticketIcon,
        ]}
      />
    );
  }, []);
  return (
    <View dataSet={{ media: ids.wrapper }} style={styles.wrapper}>
      <View dataSet={{ media: ids.firstColumn }} style={styles.firstColumn}>
        <Button onPress={() => onBookNow(data)} plain>
          <ResponsiveText
            category={textCategory.HEADING}
            style={[themeStyle.spacingBottomMedium, themeStyle.bold]}
          >
            {name}
          </ResponsiveText>
        </Button>
        <ReadMore
          category={textCategory.BODY}
          status="info"
          contentContainerStyle={themeStyle.spacingBottomMedium}
          maxChar={100}
          value={description}
        />
        <ListItem
          contentContainerStyle={themeStyle.spacingBottomXSmall}
          leftIcon={{ svg: CalendarIcon, size: 15 }}
          title={
            <ResponsiveText category={textCategory.SMALL_BODY}>
              {isLastStart
                ? startFormatted
                : DateHelper.formatDateWithText(
                    start,
                    end_date,
                    available_days,
                  )}
            </ResponsiveText>
          }
        />
        <ListItem
          contentContainerStyle={themeStyle.spacingBottomXSmall}
          leftIcon={{ name: 'clock-outline', size: 15 }}
          title={
            <ResponsiveText category={textCategory.SMALL_BODY}>
              {DateHelper.formatTimeRangeArray(time_slots)}
            </ResponsiveText>
          }
        />
        <ListItem
          contentContainerStyle={themeStyle.spacingBottomMedium}
          leftIcon={{ svg: MoneyIcon, size: 15 }}
          title={
            <ResponsiveText category={textCategory.SMALL_BODY}>
              {prices} per ticket
            </ResponsiveText>
          }
        />
        <View style={[themeStyle.flex1, themeStyle.flexDirectionRowCenter]}>
          <Button
            accessoryLeft={Accessoryleft}
            style={styles.noPadding}
            status="info"
            onPress={() => onBookNow(data)}
          >
            {() => (
              <ResponsiveText
                appearance="alternative"
                category={textCategory.SMALL_BODY}
                style={themeStyle.spacingRight}
              >
                Get tickets
              </ResponsiveText>
            )}
          </Button>
        </View>
      </View>
      <View
        dataSet={{ media: `${ids.secondColumn} ${platformStyleIds.webOnly}` }}
        style={[platformStyles.webOnly, styles.secondColumn]}
      >
        <Button onPress={() => onBookNow(data)} plain>
          <Image
            source={{ uri: image }}
            style={styles.image}
            resizeMode="cover"
          />
        </Button>
      </View>
    </View>
  );
};

export const { ids, styles } = StyleSheet.create({
  wrapper: {
    ...themeStyle.flex1,
    ...themeStyle.flexDirectionColReverse,
    [`@media ${breakpoints.sm}`]: themeStyle.flexDirectionRow,
  },
  firstColumn: {
    [`@media ${breakpoints.sm}`]: {
      flex: 1,
    },
    [`@media ${breakpoints.md}`]: {
      flex: 1.5,
      ...themeStyle.spacingRight,
    },
    [`@media ${breakpoints.lg}`]: {
      flex: 1,
    },
    [`@media ${breakpoints.xl}`]: {
      flex: 1.75,
    },
  },
  secondColumn: {
    flex: 1,
  },
  image: {
    width: '100%',
    aspectRatio: 1.75 / 1,
    borderRadius: 10,
    overflow: 'hidden',
  },
  ticketIcon: { width: 25, height: 25 },
  noPadding: { paddingVertical: 0 },
});

export default EventItem;
